import { useCallback } from 'react';
import { VALIDATION_RULES } from '../utils/constants.js';
import { WalletValidator } from '../services/walletValidator.js';

export interface FormData {
  name: string;
  email: string;
  walletAddress: string;
}

export interface ValidationRule {
  required?: string;
  minLength?: { value: number; message: string };
  maxLength?: { value: number; message: string };
  pattern?: { value: RegExp; message: string };
  validate?: (value: string) => string | boolean;
}

export interface ValidationRules {
  name: ValidationRule;
  email: ValidationRule;
  walletAddress: ValidationRule;
}

export function useFormValidation() {
  // Enhanced validation rules with custom validators
  const validationRules: ValidationRules = {
    name: {
      ...VALIDATION_RULES.name,
      validate: (value: string) => {
        if (!value.trim()) {
          return 'Name is required';
        }
        
        if (value.trim().length < 2) {
          return 'Name must be at least 2 characters';
        }
        
        if (value.trim().length > 50) {
          return 'Name must not exceed 50 characters';
        }
        
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          return 'Name can only contain letters and spaces';
        }
        
        // Check for excessive spaces
        if (/\s{2,}/.test(value)) {
          return 'Name cannot contain multiple consecutive spaces';
        }
        
        // Check for leading/trailing spaces
        if (value !== value.trim()) {
          return 'Name cannot start or end with spaces';
        }
        
        return true;
      }
    },
    
    email: {
      ...VALIDATION_RULES.email,
      validate: (value: string) => {
        if (!value.trim()) {
          return 'Email is required';
        }
        
        const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        if (!emailRegex.test(value.trim())) {
          return 'Please enter a valid email address';
        }
        
        // Additional email validation
        if (value.length > 254) {
          return 'Email address is too long';
        }
        
        // Check for common typos in domains
        const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
        const domain = value.split('@')[1]?.toLowerCase();
        
        if (domain) {
          // Check for common typos
          const typos = {
            'gmial.com': 'gmail.com',
            'gmai.com': 'gmail.com',
            'yahooo.com': 'yahoo.com',
            'hotmial.com': 'hotmail.com',
            'outlok.com': 'outlook.com'
          };
          
          if (typos[domain as keyof typeof typos]) {
            return `Did you mean ${typos[domain as keyof typeof typos]}?`;
          }
        }
        
        return true;
      }
    },
    
    walletAddress: {
      ...VALIDATION_RULES.walletAddress,
      validate: (value: string) => {
        if (!value.trim()) {
          return 'Wallet address is required';
        }
        
        // Use the WalletValidator for comprehensive validation
        const error = WalletValidator.getValidationError(value.trim());
        return error || true;
      }
    }
  };

  // Function to validate individual field
  const validateField = useCallback((fieldName: keyof FormData, value: string): string | boolean => {
    const rule = validationRules[fieldName];
    if (rule.validate) {
      return rule.validate(value);
    }
    return true;
  }, []);

  // Function to validate entire form
  const validateForm = useCallback((data: FormData): { [key: string]: string } => {
    const errors: { [key: string]: string } = {};
    
    Object.keys(data).forEach((key) => {
      const fieldName = key as keyof FormData;
      const value = data[fieldName];
      const result = validateField(fieldName, value);
      
      if (typeof result === 'string') {
        errors[fieldName] = result;
      }
    });
    
    return errors;
  }, [validateField]);

  // Function to check if form data is valid
  const isFormValid = useCallback((data: FormData): boolean => {
    const errors = validateForm(data);
    return Object.keys(errors).length === 0;
  }, [validateForm]);

  // Function to normalize form data
  const normalizeFormData = useCallback((data: FormData): FormData => {
    return {
      name: data.name.trim(),
      email: data.email.trim().toLowerCase(),
      walletAddress: WalletValidator.normalizeAddress(data.walletAddress.trim())
    };
  }, []);

  // Function to get field-specific validation rules for React Hook Form
  const getFieldValidation = useCallback((fieldName: keyof FormData) => {
    return validationRules[fieldName];
  }, []);

  return {
    validationRules,
    validateField,
    validateForm,
    isFormValid,
    normalizeFormData,
    getFieldValidation
  };
}